import { useQuery } from '@apollo/client';
import { Link } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { OutcropKeyParametersPageQuery } from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import { NotFound } from '~/components/common/NotFound';
import { Panel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { KeyParametersPanelV4 } from '~/components/key-parameters/key-parameters-panel-v4';
import {
  KPCol,
  KPGrid,
} from '~/components/key-parameters/key-parameters-panel-v4/combined-view';
import { useRouteParam } from '~/hooks/routing';
import { otherWikiDetailRoute } from '~/paths';

export const OUTCROP_KEY_PARAMETERS_PAGE = graphql(`
  query OutcropKeyParametersPage($id: ID!) {
    outcropGet(id: $id) {
      ...outcropParts
      startAge {
        ...lithostratAgeParts
      }
      endAge {
        ...lithostratAgeParts
      }
      keyParameters {
        ...keyParametersParts
      }
    }
  }
`);

export default function OutcropKeyParametersRoute() {
  return (
    <ApolloProviderV4>
      <PageInner />
    </ApolloProviderV4>
  );
}

function PageInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_KEY_PARAMETERS_PAGE, {
    variables: { id: outcropId },
  });

  const outcrop = data?.outcropGet;

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <div className="grid lg:grid-cols-3 gap-6 mt-2">
      <div className="lg:col-span-2">
        <KeyParametersPanelV4
          keyParameters={outcrop.keyParameters ?? []}
          dominantGeologyType={outcrop.dominantGeologyType}
          outcrop={outcrop}
          leftColMd={4}
          rightColMd={8}
          noHeading
          view="table"
        >
          {(kps, tabs) => (
            <>
              <div className="relative bottom-0">
                <Heading level={3}>Key Parameters</Heading>
                <div className="absolute bottom-0 right-0">{tabs}</div>
              </div>

              <Panel>
                <Panel.Body>{kps}</Panel.Body>
              </Panel>
            </>
          )}
        </KeyParametersPanelV4>
      </div>

      <div>
        <Heading level={3}>Quality Control Parameters</Heading>
        <Panel>
          <Panel.Body>
            <QualityParametersTable outcrop={outcrop} />
          </Panel.Body>
        </Panel>
      </div>
    </div>
  );
}

function KPProperty({
  label,
  value,
  wikiPageId,
}: {
  label: string;
  value: string | null | undefined;
  wikiPageId: number;
}) {
  if (typeof value === 'undefined' || value === null) {
    return null;
  }

  return (
    <KPGrid>
      <KPCol span={5}>
        <strong>{label}</strong>
      </KPCol>
      <KPCol span={7}>
        <Link
          to={otherWikiDetailRoute(wikiPageId)}
          target="_blank"
          className="link"
        >
          {value}
        </Link>
      </KPCol>
    </KPGrid>
  );
}

export function QualityParametersTable({
  outcrop,
}: {
  outcrop: NonNullable<OutcropKeyParametersPageQuery['outcropGet']>;
}) {
  return (
    <div className="space-y-0">
      <KPProperty
        label="Structural complexity"
        value={outcrop.structuralComplexity}
        wikiPageId={13}
      />
      <KPProperty
        label="Exposure quality"
        value={outcrop.exposureQuality}
        wikiPageId={9}
      />
      <KPProperty
        label="3D control"
        value={outcrop.outcrop3dControl}
        wikiPageId={6}
      />
      <KPProperty
        label="Dataset scale"
        value={outcrop.datasetScale}
        wikiPageId={8}
      />
    </div>
  );
}
